import { ExpandedState, type RowData } from "@tanstack/react-table";
import type { ViewTableInstance } from "./table";
import { useEffect, useEffectEvent, useRef } from "react";

interface UseAutoExpandOptions<TData extends RowData> {
    enabled: boolean;
    table: Pick<ViewTableInstance<TData>, "getRowModel">;
    data: TData[];
    expanded: ExpandedState;
    setExpanded: (expanded: ExpandedState) => void;
    isControlled: boolean;
}

/**
 * Hook that auto-expands all expandable rows on mount and when new groups appear.
 * Tracks which expandable rows have been seen to distinguish new rows from manually collapsed ones.
 */
export function useAutoExpand<TData extends RowData>({
    enabled,
    table,
    data,
    expanded,
    setExpanded,
    isControlled,
}: UseAutoExpandOptions<TData>): void {
    // Track which expandable rows we've seen before to distinguish new rows from manually collapsed ones
    const seenExpandableRowsRef = useRef<Set<string>>(new Set());

    const rowsLength = table.getRowModel().rows.length;

    const expandNewRows = useEffectEvent(() => {
        if (isControlled) {
            return;
        }

        const currentExpandableRowIds = new Set<string>();
        table.getRowModel().rows.forEach((row) => {
            if (row.getCanExpand()) {
                currentExpandableRowIds.add(String(row.id));
            }
        });

        // Find truly new expandable rows (ones we haven't seen before)
        const newExpandableRowIds = Array.from(currentExpandableRowIds).filter(
            (id) => !seenExpandableRowsRef.current.has(id),
        );

        if (newExpandableRowIds.length > 0) {
            // Update our tracking set
            newExpandableRowIds.forEach((id) => {
                seenExpandableRowsRef.current.add(id);
            });

            // Expand only the new rows
            const newExpandedState = {
                ...(expanded as Record<string, boolean>),
            };
            newExpandableRowIds.forEach((id) => {
                newExpandedState[id] = true;
            });
            setExpanded(newExpandedState);
        }
    });

    useEffect(() => {
        if (!enabled || data.length === 0 || rowsLength === 0) {
            return;
        }
        expandNewRows();
    }, [enabled, data, rowsLength]);
}
